import { decodeBase64Url } from 'admin-panel/utils/base64-url';
import {
  composePageContent,
  getPageHeaderImageLegacyTop,
  getPageHeaderImageViewportHeight,
  parsePageHeaderImage,
} from 'admin-panel/utils/page-header-image';

import type { TranslatedField } from 'admin-panel/constants/types';
import type { PageHeaderImageBlock } from 'admin-panel/utils/page-header-image';

export type PageHeaderImageSharingMode = 'shared' | 'language-specific';
export type LegacyImageUidLookup = Readonly<Record<string, string | undefined>>;

export type PageHeaderImageSharingClassification = {
  mode: PageHeaderImageSharingMode;
  sharedHeader: PageHeaderImageBlock | null;
  signatures: Record<string, string>;
};

function imageIdentifierFromPath(pathname: string) {
  const match = pathname.match(/\/image\/([^/]+)\/(?:versions|resized)\//i);

  if (!match?.[1]) return null;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

function isTrustedSuitePadHostname(hostname: string) {
  const normalized = hostname.toLowerCase();

  return ['suitepad.de', 'suitepad.systems', 'suitepad.engineering'].some(
    (domain) => normalized === domain || normalized.endsWith(`.${domain}`)
  );
}

function unsupportedLegacyPositionSignature(header: PageHeaderImageBlock) {
  if (header.cropY !== null || getPageHeaderImageLegacyTop(header) !== null) {
    return null;
  }

  const parsedDocument = new DOMParser().parseFromString(
    header.html,
    'text/html'
  );
  const image = parsedDocument.querySelector('img');
  const top = image?.style.top ?? '';
  const transform = image?.style.transform ?? '';

  if (!top && !transform) return null;

  return {
    top,
    topPriority: image?.style.getPropertyPriority('top') ?? '',
    transform,
    transformPriority: image?.style.getPropertyPriority('transform') ?? '',
  };
}

function decodedImgproxySource(source: URL) {
  if (
    !source.hostname.toLowerCase().startsWith('img.') ||
    !isTrustedSuitePadHostname(source.hostname)
  ) {
    return null;
  }

  const segments = source.pathname.split('/').filter(Boolean);

  for (let index = segments.length - 1; index >= 0; index--) {
    const segment = segments[index];
    if (!segment) continue;

    const candidates = [segment, segment.replace(/\.[a-z0-9]+$/i, '')];

    for (const candidate of candidates) {
      try {
        const decoded = decodeBase64Url(candidate);
        const decodedUrl = new URL(decoded);

        if (
          decodedUrl.protocol === 'http:' ||
          decodedUrl.protocol === 'https:'
        ) {
          return decodedUrl;
        }
      } catch {
        // Imgproxy path segments also contain resize options and signatures.
      }
    }
  }

  return null;
}

export function normalizedPageHeaderImageIdentity(
  source: string,
  legacyImageUids: LegacyImageUidLookup = {}
) {
  let url: URL;

  try {
    url = new URL(source);
  } catch {
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

  const resolvedSource = decodedImgproxySource(url) ?? url;
  const legacyImageId = isTrustedSuitePadHostname(resolvedSource.hostname)
    ? imageIdentifierFromPath(resolvedSource.pathname)
    : null;

  if (legacyImageId) {
    const uid = legacyImageUids[legacyImageId];

    if (uid) return `uid:${uid}`;

    return `suitepad-image:${legacyImageId}`;
  }

  resolvedSource.hash = '';

  return `url:${resolvedSource.href}`;
}

export function pageHeaderImageSignature(
  header: PageHeaderImageBlock,
  legacyImageUids: LegacyImageUidLookup = {}
) {
  const identity = normalizedPageHeaderImageIdentity(
    header.src,
    legacyImageUids
  );

  if (!identity) return null;

  return JSON.stringify({
    identity,
    height: getPageHeaderImageViewportHeight(header),
    cropY: header.cropY,
    legacyTop: getPageHeaderImageLegacyTop(header),
    unsupportedLegacyPosition: unsupportedLegacyPositionSignature(header),
    cropInvalid: header.cropInvalid,
  });
}

export function pageHeaderImagesAreEquivalent(
  first: PageHeaderImageBlock,
  second: PageHeaderImageBlock,
  legacyImageUids: LegacyImageUidLookup = {}
) {
  const firstSignature = pageHeaderImageSignature(first, legacyImageUids);
  const secondSignature = pageHeaderImageSignature(second, legacyImageUids);

  return Boolean(firstSignature && firstSignature === secondSignature);
}

export function classifyPageHeaderImageSharing(
  translations: TranslatedField,
  legacyImageUids: LegacyImageUidLookup = {}
): PageHeaderImageSharingClassification {
  const signatures: Record<string, string> = {};
  let sharedHeader: PageHeaderImageBlock | null = null;
  let expectedSignature: string | null | undefined;
  let hasConflict = false;

  for (const [locale, content] of Object.entries(translations)) {
    if (content === null) continue;

    const header = parsePageHeaderImage(content).headerImage;
    const signature = header
      ? pageHeaderImageSignature(header, legacyImageUids)
      : null;

    signatures[locale] = signature ?? 'no-header';
    if (header && !sharedHeader) sharedHeader = header;

    if (expectedSignature === undefined) {
      expectedSignature = signature;
    } else if (signature !== expectedSignature) {
      hasConflict = true;
    }
  }

  return {
    mode: hasConflict ? 'language-specific' : 'shared',
    sharedHeader,
    signatures,
  };
}

export function replacementHeaderCount(
  translations: TranslatedField,
  activeLocale: string,
  adoptedHeader: PageHeaderImageBlock,
  legacyImageUids: LegacyImageUidLookup = {}
) {
  return Object.entries(translations).reduce((count, [locale, content]) => {
    if (locale === activeLocale || content === null) return count;

    const existingHeader = parsePageHeaderImage(content).headerImage;

    return existingHeader &&
      !pageHeaderImagesAreEquivalent(
        existingHeader,
        adoptedHeader,
        legacyImageUids
      )
      ? count + 1
      : count;
  }, 0);
}

export function applySharedPageHeader(
  translations: TranslatedField,
  header: PageHeaderImageBlock | null
) {
  const result: TranslatedField = {};

  for (const [locale, content] of Object.entries(translations)) {
    if (content === null) {
      result[locale] = null;
      continue;
    }

    const parsed = parsePageHeaderImage(content);

    result[locale] = parsed.headerImage
      ? composePageContent(parsed.bodyHtml, header, parsed.prefixHtml)
      : header
      ? `${header.html}${content}`
      : content;
  }

  return result;
}
